import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  AlertTitle,
  Chip
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CloudIcon from '@mui/icons-material/Cloud';
import GroupIcon from '@mui/icons-material/Group';

interface ApiPermissionsScenarioProps {
  onBack: () => void;
}

const ApiPermissionsScenario: React.FC<ApiPermissionsScenarioProps> = ({ onBack }) => {
  const [selectedPermissionType, setSelectedPermissionType] = useState<string | null>(null);

  const permissionTypes = [
    {
      id: "microsoft-graph",
      title: "Microsoft Graph Permissions",
      description: "Add common Microsoft Graph permissions (User.Read, Mail.Read, etc.)",
      icon: <CloudIcon color="primary" />,
      complexity: "Beginner"
    },
    {
      id: "custom-api",
      title: "Custom API Permissions", 
      description: "Configure permissions for your own APIs and third-party services",
      icon: <VpnKeyIcon color="primary" />,
      complexity: "Intermediate"
    },
    {
      id: "admin-consent",
      title: "Admin Consent Management",
      description: "Understand and manage admin consent for application permissions",
      icon: <GroupIcon color="secondary" />,
      complexity: "Advanced"
    }
  ];

  const renderPermissionTypeContent = () => {
    if (selectedPermissionType === "microsoft-graph") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Configure Microsoft Graph Permissions
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Most Common Permission Type</AlertTitle>
            <Typography variant="body2">
              Microsoft Graph permissions allow your application to access Microsoft 365 services like 
              user profiles, email, calendar, and files. This covers both delegated (user context) 
              and application (app-only) permissions.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            This scenario walks you through adding Microsoft Graph permissions using AppConfig²'s 
            API Permissions interface, understanding the difference between delegated and application 
            permissions, and testing that permissions work correctly.
          </Typography>
          
          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 1 }}>
              📋 Key Concepts
            </Typography>
            <Typography variant="body2">
              • <strong>Delegated Permissions:</strong> App acts on behalf of a signed-in user<br/>
              • <strong>Application Permissions:</strong> App acts with its own identity (no user context)<br/>
              • <strong>Admin Consent:</strong> Some permissions require administrator approval<br/>
              • <strong>Effective Permissions:</strong> Intersection of app permissions and user's actual permissions
            </Typography>
          </Box>

          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>Navigate to API Permissions</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Access the API permissions configuration for your application:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Navigate to Applications section using the main sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Find and select your application" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Select' to open the App Tester" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the App Tester sidebar, click 'API Permissions'" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Add Microsoft Graph Delegated Permission</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Let's add a common delegated permission like User.Read:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Expand the 'Microsoft Graph Permissions' section" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add Microsoft Graph Permission'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select 'Delegated (Scope)' permission type" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Search for 'User.Read' in the permission list" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Check the 'User.Read' permission and click 'Add Permission'" />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    What User.Read Allows
                  </Typography>
                  <Typography variant="body2">
                    • Read the signed-in user's profile information<br/>
                    • Access basic user details like name, email, and photo<br/>
                    • No admin consent required (standard permission)
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Add Additional Graph Permissions</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Let's add a few more common permissions to see different consent requirements:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Add 'Mail.Read' (delegated)" 
                      secondary="Allows reading user's email messages"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Add 'Calendars.Read' (delegated)" 
                      secondary="Allows reading user's calendar events"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Try adding 'User.ReadWrite.All' (application)" 
                      secondary="Requires admin consent - allows reading/writing all users"
                    />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#fff8e6', borderRadius: 1, border: '1px solid #ffd87c' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Permission Descriptions
                  </Typography>
                  <Typography variant="body2">
                    Always read permission descriptions carefully. Some permissions like 'User.ReadWrite.All' 
                    can access sensitive data and require careful consideration and admin consent.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Apply Changes</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Save your API permission configuration:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Apply Changes' to save the permission configuration" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Wait for the confirmation message" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Review the permissions list to ensure all permissions were added" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Test Permissions with Token Request</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Verify that your permissions work by requesting tokens:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Navigate to 'Fetch Access Token' in the App Tester sidebar" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In 'Select API Audience', choose 'Microsoft Graph'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select scopes corresponding to your permissions (User.Read, Mail.Read, etc.)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Start Authentication' and complete the sign-in flow" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In 'Active Tokens', examine the access token for Microsoft Graph" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify the 'scp' claim contains your requested scopes" />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#e6f4ea', borderRadius: 1, border: '1px solid #acebc0' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Expected Token Content
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', bgcolor: '#fafafa', p: 1, borderRadius: 1 }}>
                    {`{
  "aud": "https://graph.microsoft.com",
  "scp": "User.Read Mail.Read Calendars.Read",
  "app_displayname": "Your App Name",
  ...
}`}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      );
    }

    if (selectedPermissionType === "custom-api") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Configure Custom API Permissions
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Requires API Setup</AlertTitle>
            <Typography variant="body2">
              To configure custom API permissions, you need an API application that exposes scopes. 
              If you haven't created one yet, see our "Exposing & Consuming APIs" workflow first.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Custom API permissions allow your client applications to access your own APIs or third-party 
            APIs registered in your tenant. This scenario shows you how to configure these permissions 
            and validate they work correctly.
          </Typography>

          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>Access Custom API Permissions</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Navigate to the custom API permissions section:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the API Permissions interface, scroll to 'Custom API Permissions'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add Custom API Permission'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="You'll see a dialog to find and configure API permissions" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Find Your API</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Locate the API you want to configure permissions for:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Enter your API's Application (client) ID in the 'API Client ID' field" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Find Scopes' to discover available permissions" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Review the list of exposed scopes and roles" />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#fff8e6', borderRadius: 1, border: '1px solid #ffd87c' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Finding API Client IDs
                  </Typography>
                  <Typography variant="body2">
                    You can find API client IDs in the Applications section of AppConfig² or in the 
                    Azure portal under "App registrations". Look for the "Application (client) ID" value.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Select Permissions</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Choose the specific permissions your application needs:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Choose between 'Delegated (Scope)' or 'Application (Role)' permissions" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Select the specific scopes your application needs" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Review permission descriptions to understand what each allows" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click 'Add Permission' to save the selection" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Test Custom API Access</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Verify that your custom API permissions work:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Apply changes to save the permission configuration" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Navigate to 'Fetch Access Token'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In 'Select API Audience', choose your custom API" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Authenticate and examine the resulting access token" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify the 'aud' claim matches your API and 'scp' contains your scopes" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      );
    }

    if (selectedPermissionType === "admin-consent") {
      return (
        <Box>
          <Typography variant="h5" gutterBottom color="primary">
            Understand Admin Consent Management
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Critical for Enterprise Apps</AlertTitle>
            <Typography variant="body2">
              Admin consent is required for application permissions and some high-privilege delegated permissions. 
              Understanding this process is essential for enterprise application deployment.
            </Typography>
          </Alert>
          
          <Typography variant="body1" paragraph>
            Admin consent allows administrators to grant permissions on behalf of all users in the organization. 
            This scenario explains when admin consent is required, how to grant it, and how to verify consent status.
          </Typography>

          <Stepper orientation="vertical">
            <Step active>
              <StepLabel>Understanding Consent Requirements</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Learn which permissions require admin consent:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Application Permissions" 
                      secondary="All application permissions require admin consent"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="High-Privilege Delegated Permissions" 
                      secondary="Permissions like Directory.ReadWrite.All, User.ReadWrite.All"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Custom API Permissions" 
                      secondary="May require admin consent depending on configuration"
                    />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Check Current Consent Status</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Review the consent status of your application's permissions:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="In the API Permissions interface, review the 'Status' column" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Look for permissions marked as 'Not granted' or 'Requires admin consent'" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Note which permissions need administrative approval" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Grant Admin Consent (if you have permissions)</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  If you're an administrator, you can grant consent directly:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Look for 'Grant admin consent' button in the API Permissions interface" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Click the button to grant consent for all permissions" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Review the consent dialog carefully before confirming" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify that permission status changes to 'Granted'" />
                  </ListItem>
                </List>
                <Box sx={{ my: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1, border: '1px solid #ffcdd2' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    ⚠️ Admin Consent Considerations
                  </Typography>
                  <Typography variant="body2">
                    Admin consent grants permissions for the entire organization. Only grant consent for 
                    permissions that are truly necessary for your application's functionality.
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Continue</Button>
                </Box>
              </StepContent>
            </Step>

            <Step active>
              <StepLabel>Test with Different Users</StepLabel>
              <StepContent>
                <Typography variant="body2" paragraph>
                  Validate that admin consent works for different user types:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Test authentication with a regular user account" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify that no additional consent prompts appear" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Check that access tokens contain the expected scopes" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Test with users who have different permission levels" />
                  </ListItem>
                </List>
                <Box sx={{ mb: 2 }}>
                  <Button variant="contained" size="small">Finish</Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      );
    }

    return null;
  };

  if (selectedPermissionType) {
    return (
      <Box>
        {renderPermissionTypeContent()}
        <Box sx={{ mt: 4 }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowRightIcon />}
            onClick={() => setSelectedPermissionType(null)}
          >
            Back to Permission Types
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom color="primary">
        Configure API Permissions
      </Typography>
      
      <Typography variant="body1" paragraph>
        API permissions determine what resources your application can access on behalf of users 
        or using its own identity. This scenario covers the most common permission configuration 
        scenarios using AppConfig²'s API Permissions interface.
      </Typography>
      
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f9ff', borderLeft: '4px solid #0078d4', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#0078d4', mb: 1 }}>
          What You'll Learn
        </Typography>
        <Typography variant="body2">
          • Difference between delegated and application permissions<br/>
          • How to configure Microsoft Graph permissions<br/>
          • How to add custom API permissions<br/>
          • Understanding and managing admin consent<br/>
          • Testing permissions with token requests
        </Typography>
      </Box>

      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Choose a Permission Scenario
      </Typography>
      
      <Grid container spacing={3}>
        {permissionTypes.map((type) => (
          <Grid item xs={12} md={4} key={type.id}>
            <Card 
              variant="outlined"
              sx={{ 
                cursor: 'pointer',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s'
                }
              }}
              onClick={() => setSelectedPermissionType(type.id)}
            >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {type.icon}
                  <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                    {type.title}
                  </Typography>
                  <Chip 
                    label={type.complexity} 
                    size="small" 
                    color={
                      type.complexity === 'Beginner' ? 'success' : 
                      type.complexity === 'Intermediate' ? 'warning' : 'error'
                    }
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {type.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Permission Best Practices
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText 
              primary="Principle of Least Privilege" 
              secondary="Only request permissions your application actually needs"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText 
              primary="User vs Application Context" 
              secondary="Choose delegated permissions for user actions, application permissions for background tasks"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText 
              primary="Test Thoroughly" 
              secondary="Always test permissions with different user types and scenarios"
            />
          </ListItem>
        </List>
      </Box>

      <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Common Permission Patterns
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText 
              primary="Basic User Profile Access" 
              secondary="User.Read, profile, openid, email - for basic authentication"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText 
              primary="Email and Calendar Integration" 
              secondary="Mail.Read, Mail.Send, Calendars.Read - for productivity apps"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText 
              primary="Administrative Access" 
              secondary="Directory.ReadWrite.All, User.ReadWrite.All - for admin tools"
            />
          </ListItem>
        </List>
      </Box>
      
      <Box sx={{ mt: 4 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowRightIcon />}
          onClick={onBack}
        >
          Back to All Scenarios
        </Button>
      </Box>
    </Box>
  );
};

export default ApiPermissionsScenario;